<template>
    <div>
        <calendar :events="my_time_off" :holidays="holidays" @click="request"></calendar>

        <modal-dialog
            id="time-off-request-editor"
            :show.sync="editing"
        >
            <template v-slot:title>
                <h2>Request Time Off</h2>
            </template>
            <template v-slot:footer>
                <div class="uk-grid">
                    <div v-if="!!edited_event.id" class="uk-width-1-3">
                        <base-button
                            :waiting="deleting"
                            :disabled="deleting||saving"
                            color="danger"
                            size="large"
                            width="1-1"
                            @click.prevent="remove"
                        >
                            Delete
                        </base-button>
                    </div>
                    <div :class="{'uk-width-1-2': !edited_event.id, 'uk-width-1-3': !!edited_event.id}">
                        <base-button
                            :disabled="deleting||saving"
                            size="large"
                            width="1-1"
                            @click.prevent="close"
                        >
                            Cancel
                        </base-button>
                    </div>
                    <div :class="{'uk-width-1-2': !edited_event.id, 'uk-width-1-3': !!edited_event.id}">
                        <base-button
                            :waiting="saving"
                            :disabled="deleting||saving"
                            color="primary"
                            size="large"
                            width="1-1"
                            @click.prevent="save"
                        >
                            <span v-if="!editable">Send Request</span>
                            <span v-else>OK</span>
                        </base-button>
                    </div>
                </div>
            </template>

            <form v-if="!!edited_event" class="uk-form" role="form">
                <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                    <div class="uk-width-1-1 uk-text-center">
                        <form-date-range-control
                            id="request"
                            name="requested_starts_at"
                            end-name="requested_ends_at"
                            calendar-position="bottom"
                            :min-date="!!edited_event.id?false:0"
                            v-model="edited_event"
                            :editable="editable"
                            :required="true"
                        />
                    </div>
                </div>
                <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                    <div class="uk-width-1-5 uk-text-right lam-label-column">
                        Type
                    </div>
                    <div class="uk-width-4-5">
                        <select v-model="edited_event.benefit" :disabled="!editable" class="uk-width-1-1">
                            <option v-for="benefit in user_benefits(true)" :key="benefit.id" :value="benefit.id">{{ benefit.label }}</option>
                        </select>
                    </div>
                </div>

                <div class="uk-grid uk-grid-small" data-uk-grid-margin>
                    <div class="uk-width-1-1">
                        <h5 v-if="explanation_required">Explanation Required</h5>
                        <h5 v-else>Message</h5>
                        <textarea
                            v-model="edited_event.message"
                            :disabled="!editable"
                            class="uk-width-1-1"
                        ></textarea>
                    </div>
                </div>
            </form>
        </modal-dialog>
    </div>
</template>

<script type="text/javascript">
import { mapState, mapGetters } from "vuex"

import profile_utilities from "../../mixins/ProfileUtilities"
import dashboard_card from "./dashboard_card"

import ModalDialog from "../../dialogs/ModalDialog/ModalDialog"
import Calendar from "../../ui/Calendar"
import FormDateRangeControl from "../../ui/FormDateRangeControl"
import BaseButton from "../../ui/BaseButton"

export default {
    mixins: [profile_utilities, dashboard_card],
    components: {
        ModalDialog,
        Calendar,
        FormDateRangeControl,
        BaseButton
    },
    mounted () {
        this.load_data()
    },
    watch: {
        profile_id: "load_data",
        $route: "load_data"
    },
    methods: {
        load_data () {
            if(this.profile_id) {
                this.loading = true
                this.$store.dispatch("FETCH_RECORDS", { entity: this.card.entity }).then(() => {
                    this.$store.dispatch("DASHBOARD_DATA", { entity: this.card.entity, data_id: this.card.id }).then(() => {
                        // all good!
                    }).catch(error => {
                        console.error(error)
                    })
                }).catch(error => {
                    console.error(error)
                }).then(() => {
                    this.loading = false
                })
            }
        },
        request (date, event) {
            this.edited_event = event ? { ...event } : {
                id: 0,
                user_id: this.profile_id,
                requested_starts_at: this.$moment(date),
                requested_ends_at: this.$moment(date).add(1, "days"),
                benefit: this.user_benefits(true)[0].id,
                message: ""
            }
            this.editing = true
        },
        save () {
            this.saving = true
            this.edited_event.requested_starts_at = this.$moment(this.edited_event.requested_starts_at).format("YYYY-MM-DD")
            this.edited_event.requested_ends_at = this.$moment(this.edited_event.requested_ends_at).format("YYYY-MM-DD")

            if(this.edited_event.id) {
                this.$store.dispatch("RECORD_SAVE", {
                    entity: "time_off_request",
                    id: this.edited_event.id,
                    data: this.edited_event
                }).then(() => {
                    this.close()
                }).catch(error => {
                    if(error) console.log(error)
                }).then(() => {
                    this.saving = false
                })
            } else {
                this.$store.dispatch("CREATE_RECORD", {
                    entity: "time_off_request",
                    data: this.edited_event
                }).then(() => {
                    this.close()
                }).catch(error => {
                    if(error) {
                        if(error.topic_id === "message") error.message = "An explanation is required for this type of time off."
                        this.$error(error.message)
                    }
                }).then(() => {
                    this.saving = false
                })
            }
        },
        remove () {
            const range = this.$moment(this.edited_event.requested_starts_at).format("YYYY-MM-DD") + " - " + this.$moment(this.edited_event.requested_ends_at).format("YYYY-MM-DD")
            this.$confirm({
                type: "error",
                title: "Delete Time Off Request",
                message: `Do you really want to delete the request for ${range}? There is no undo...`,
                cancel: "Keep It",
                ok: "Delete It",
                then: (confirmed) => {
                    if(confirmed) {
                        this.$store.dispatch("RECORD_DELETE", {
                            entity: "time_off_request",
                            id: this.edited_event.id
                        }).catch(error => {
                            if(error) this.$error(error.message)
                        }).then(() => {
                            this.close()
                        })
                    }
                }
            })
        },
        close () {
            this.editing = false
            this.edited_event = null
            this.saving = false
            this.deleting = false
        }
    },
    computed: {
        ...mapState(["holidays"]),
        ...mapGetters(["entity_records"]),
        raw_records () {
            return this.entity_records("time_off_request")
        },
        my_time_off () {
            if(!this.profile_id) return []
            return this.raw_records.filter(time_off_request => {
                return time_off_request.user_id === this.profile_id
            })
        },
        editable () {
            if(!this.edited_event) return false
            if(this.edited_event.user_id === this.profile_id) return !this.edited_event.id || (this.edited_event.request_status === "pending")
            return false
        },
        explanation_required () {
            if(!this.edited_event || !this.edited_event.benefit) return false
            return !!this.edited_event.benefit.match(/^(professional_development_hours|business_development)$/)
        }
    },
    data () {
        return {
            editing: false,
            edited_event: null,
            saving: false,
            deleting: false
        }
    }
}
</script>
